import { SelectItemType } from '../components/select-with-search/select-item.type';
import { replace } from '../helpers/array.helper';
import { DateRange } from '../types/date-range.type';
import { FilteringHelper } from './filtering.helper';

export enum FilterName {
  Active = 'active',
  Category = 'category',
  ConditionType = 'condition-type',
  Domain = 'domain',
  EntityName = 'entityName',
  Exception = 'exception',
  ExpertEntityName = 'expertEntityName',
  ExternalSystem = 'external-system',
  Feedback = 'feedback',
  Impact = 'impact',
  IsDeleted = 'is-deleted',
  LoginUser = 'login-user',
  ManualStart = 'manualStart',
  Metric = 'metric',
  MonitoringFolder = 'monitoring-folder',
  Process = 'process',
  PropertyName = 'propertyName',
  Queue = 'queue',
  Resource = 'resource',
  HasResourceGroup = 'has-resource-group',
  ResourceGroup = 'resource-group',
  Search = 'search',
  Status = 'status',
  CustomStatus = 'custom-status',
  Tags = 'tags',
  Tenant = 'tenant',
  TimePeriod = 'time-period',
  User = 'user',
  UtilizationInterval = 'utilization-interval',
  Visibility = 'visibility',
}

export const TagFilter = {
  noTag: { id: 'noTag', label: 'Not tagged', type: SelectItemType.Explicit } as FilterFixedValue,
  anyTag: { id: 'anyTag', label: 'Any tag', type: SelectItemType.All } as FilterFixedValue,
};

export const ResourceGroupFilter = {
  anyGroup: { id: 'anyGroup', label: 'Any machine group', type: SelectItemType.Exclusive } as FilterFixedValue,
  noGroup: { id: 'noGroup', label: 'Machines without a machine group', type: SelectItemType.Explicit } as FilterFixedValue,
};

export interface FilterItemValue {
  id: any;
  label: string;
  queryParamName?: string;
  queryParamValue?: any;
  count?: number;
  disabled?: boolean;
  group?: string;
  description?: string;
}

export interface FilterFixedValue {
  id: string;
  label: string;
  type: SelectItemType;
  count?: number;
  disabled?: boolean;
}

export enum FilterItemType {
  Simple,
  Select,
  MultiSelect,
  Checkbox,
}

export interface BaseFilterItem {
  type: FilterItemType;
  name: string;
  queryParamName?: string;
  getTooltip?: () => string;
}

export interface SimpleFilterItem extends BaseFilterItem {
  type: FilterItemType.Simple;
  value?: any;
  defaultValue?: any;
}

export interface SelectFilterItem extends BaseFilterItem {
  type: FilterItemType.Select;
  value?: any;
  defaultValue?: any;
  items: FilterItemValue[];
  fixedItems?: FilterFixedValue[];
  selectAllLabel?: string;
  placeholder?: string;
  sortItemsByLabel?: boolean;
}

export interface MultiSelectFilterItem extends BaseFilterItem {
  type: FilterItemType.MultiSelect;
  value?: any[];
  defaultValue?: any[];
  items: FilterItemValue[];
  fixedItems?: FilterFixedValue[];
  selectAllLabel?: string;
  placeholder?: string;
  clearFilterIfAllSelected?: boolean;
  allowEmptyArray?: boolean;
  sortItemsByLabel?: boolean;
}

export interface CheckboxFilterItem extends BaseFilterItem {
  type: FilterItemType.Checkbox;
  label: string;
  value?: boolean;
  defaultValue?: boolean;
}

export type FilterItem = SimpleFilterItem | SelectFilterItem | MultiSelectFilterItem | CheckboxFilterItem;

export class FilterDefinition {
  constructor(public filters: FilterItem[]) {}

  isFilterActive(name: string): boolean {
    const filter = this.filters.find(filter => filter.name === name);
    return filter?.value != null;
  }

  isAnyFilterActive(names: string[]): boolean {
    return names?.some(name => this.isFilterActive(name));
  }

  getFilterValue<T extends boolean | string | string[] | DateRange | null>(name: string): T {
    const filter = this.filters.find(f => f.name === name);
    if (filter == null) {
      return null;
    }

    return filter.value as T;
  }

  getFilterValues(names: string[]): any {
    return names.reduce((acc, name) => ({ ...acc, [name]: this.getFilterValue(name) }), {});
  }

  // For storing filter data into filterStorage as Record<string, any>
  toFilterRecord(): Record<string, any> {
    const result: Record<string, any> = {};
    this.filters.forEach(f => {
      result[f.name] = f.value;
    });
    return result;
  }

  getFilter(name: string): FilterItem | null {
    return this.filters.find(f => f.name === name);
  }

  updateFilter(name: string, filter: FilterItem): void {
    const oldFilter = this.getFilter(name);
    replace(this.filters, oldFilter, filter);
  }

  static createSelect(name: string, items: FilterItemValue[], selectAllLabel: string, value: string | boolean | null, queryParamName: string = null, placeholder: string = null, defaultValue?: string | boolean | null, getTooltip?: () => string, sortItemsByLabel = true): SelectFilterItem {
    const computedDefaultValue: string | boolean | null = defaultValue !== undefined ? defaultValue : (selectAllLabel != null ? null : (items[0]?.id as string | null ?? null));
    return this.createFilterItem(FilterItemType.Select, name, items, selectAllLabel, value, computedDefaultValue, queryParamName, placeholder, getTooltip, false, sortItemsByLabel) as SelectFilterItem;
  }

  static createMultiSelect(name: string, items: FilterItemValue[], selectAllLabel: string, value: string | string[], queryParamName: string = null, placeholder: string = null, defaultValue: string | string[] = null, allowEmptyArray = false, sortItemsByLabel = true): MultiSelectFilterItem {
    return this.createFilterItem(FilterItemType.MultiSelect, name, items, selectAllLabel, value, defaultValue, queryParamName, placeholder, undefined, allowEmptyArray, sortItemsByLabel) as MultiSelectFilterItem;
  }

  static createCheckbox(name: string, label: string, value: boolean, queryParamName: string = null, defaultValue?: boolean, getTooltip?: () => string): CheckboxFilterItem {
    const computedDefaultValue = defaultValue !== undefined ? defaultValue : false;
    return this.createFilterItem(FilterItemType.Checkbox, name, null, null, value, computedDefaultValue, queryParamName, null, getTooltip, false, true, label) as CheckboxFilterItem;
  }

  static createFilterItem(
    type: FilterItemType,
    name: string,
    items: FilterItemValue[],
    selectAllLabel: string | null,
    value: string | string[] | boolean | DateRange | null,
    // defaultValue = undefined means the filter should use first item or 'all' option and that this filter is not affected by 'clear all filters' action!
    defaultValue: string | string[] | boolean | DateRange | null,
    queryParamName: string = null,
    placeholder: string = null,
    getTooltip?: () => string,
    allowEmptyArray = false,
    sortItemsByLabel = true,
    label?: string,
  ): FilterItem {
    const fixedItems: FilterFixedValue[] = type === FilterItemType.Select && selectAllLabel != null
      ? [{ id: FilteringHelper.AllItemsId, label: selectAllLabel, type: SelectItemType.All }]
      : [];

    const baseFilter = { type, name, items, fixedItems, selectAllLabel, value, defaultValue, queryParamName, placeholder, getTooltip, sortItemsByLabel, label };
    if (type === FilterItemType.MultiSelect) {
      return { ...baseFilter, clearFilterIfAllSelected: true, allowEmptyArray: allowEmptyArray } as MultiSelectFilterItem;
    }
    return baseFilter as FilterItem;
  }
}
